import React from 'react';
import { useEffect, useRef } from 'react';

import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useTranslation } from 'react-i18next';
import {
    CREDIT_APP_BUTTON_CONTINUE,
    CREDIT_APP_EDIT_LABEL,
    CREDIT_APP_SAVE_BUTTON,
    PAYMENT_INFORMATION_DETAILS_BUTTONS_EDIT,
} from '../../../localization/resouceKeys';
import './style.scss';

// Bronson .c-form-section__panel transition is 0.5s, plus a small buffer.
const SECTION_TRANSITION_MS = 600;

interface IProps {
    children: any;
    title: string;
    handlerSubmit: Function;
    isOpen?: boolean;
    setOpen?: any;
    showIcon: boolean | null;
    isDisabled?: boolean;
    onEdit?: Function;
    isBlocked?: boolean;
    showPrint?: boolean;
    isEditMode?: boolean;
    disablePrintAndSubmitBtn: Function;
    focusOnOpen?: boolean;
}

const FormSectionPage: React.FC<IProps> = ({
    children,
    title,
    handlerSubmit,
    isOpen = false,
    setOpen,
    showIcon,
    isDisabled = false,
    onEdit,
    isBlocked = false,
    isEditMode = false,
    disablePrintAndSubmitBtn,
    focusOnOpen = true,
}: any) => {
    const { t } = useTranslation();
    const { markCreditFormTouched } = useCreditForm();
    const sectionRef = useRef<HTMLElement>(null);
    const wasOpen = useRef(false);

    useEffect(() => {
        if (isOpen && !wasOpen.current && focusOnOpen) {
            const firstField = sectionRef.current?.querySelector<HTMLElement>(
                '.c-form-section__panel input:not([type="hidden"]):not([disabled]), .c-form-section__panel select:not([disabled]), .c-form-section__panel textarea:not([disabled]), .c-form-section__panel [role="combobox"][tabindex]:not([aria-disabled="true"])',
            );

            const focusFirstField = () => {
                if (!sectionRef.current?.contains(document.activeElement)) {
                    firstField?.focus({ preventScroll: true });
                }
            };

            const scrollToSectionTop = () => {
                if (!sectionRef.current) return;
                const sectionTop =
                    sectionRef.current.getBoundingClientRect().top +
                    window.scrollY;
                const topBarHeight =
                    document
                        .querySelector('.c-section-nav')
                        ?.getBoundingClientRect().height ?? Number('0');

                window.scrollTo({
                    top: Math.max(Number('0'), sectionTop - topBarHeight),
                    behavior: 'auto',
                });
            };

            focusFirstField();
            scrollToSectionTop();

            // The previous section collapses with a CSS transition and shifts this one up, so keep re-aligning until it settles.
            const alignUntil = Date.now() + SECTION_TRANSITION_MS;
            let frameId = Number('0');
            const stopAligning = () => {
                window.cancelAnimationFrame(frameId);
                window.removeEventListener('wheel', stopAligning);
                window.removeEventListener('touchstart', stopAligning);
            };
            const keepAligned = () => {
                scrollToSectionTop();
                if (Date.now() < alignUntil) {
                    frameId = window.requestAnimationFrame(keepAligned);
                } else {
                    stopAligning();
                    focusFirstField();
                }
            };
            frameId = window.requestAnimationFrame(keepAligned);
            window.addEventListener('wheel', stopAligning, { passive: true });
            window.addEventListener('touchstart', stopAligning, {
                passive: true,
            });

            wasOpen.current = isOpen;
            return stopAligning;
        }
        wasOpen.current = isOpen;
    }, [isOpen]);

    return (
        <section
            ref={sectionRef}
            className={`c-form-section  js-form-section ${
                isOpen ? 'is-open' : ''
            } ${
                showIcon !== null
                    ? showIcon
                        ? 'c-form-section--success'
                        : 'c-form-section--error'
                    : ''
            }`}
        >
            <button
                className='c-form-section__header  js-form-section__header'
                aria-expanded={`${!isOpen ? 'false' : 'true'}`}
                onClick={() => {
                    setOpen(!isOpen);
                    if (isEditMode && !showIcon) {
                        disablePrintAndSubmitBtn(true);
                    }
                }}
                disabled={isBlocked}
            >
                <span className='c-form-section__header-content'>
                    <span className='c-form-section__title  js-form-section__trigger'>
                        <span
                            className='c-form-section__title-text form-section__title-font'
                            role='heading'
                        >
                            {title}
                        </span>
                    </span>
                </span>
                {isEditMode && !showIcon && (
                    <span className='editLabel'>
                        {t(CREDIT_APP_EDIT_LABEL)}
                    </span>
                )}
                {showIcon !== null ? (
                    <span className='c-form-section__header-icon'></span>
                ) : null}
            </button>

            <div
                className={`c-form-section__panel  js-form-section__panel ${
                    isOpen ? 'is-expanded' : ''
                }`}
                aria-hidden={!isOpen}
                hidden={!isOpen}
            >
                <div className='c-form-section__content'>
                    <form className='js-floating-label  ' action='#0'>
                        <fieldset className='o-fieldset'>{children}</fieldset>
                        <div className='o-fieldset'>
                            {!isDisabled && (
                                <div className='o-fieldset__row action-wrapper'>
                                    <button
                                        className='c-btn'
                                        type='button'
                                        onClick={() => {
                                            markCreditFormTouched();
                                            handlerSubmit();
                                        }}
                                    >
                                        <span className='c-btn__text'>
                                            {isEditMode
                                                ? t(CREDIT_APP_SAVE_BUTTON)
                                                : t(CREDIT_APP_BUTTON_CONTINUE)}
                                        </span>
                                    </button>
                                </div>
                            )}
                            {isDisabled && (
                                <div className='o-fieldset__row'>
                                    <button
                                        className='c-btn c-btn--secondary'
                                        type='button'
                                        onClick={onEdit}
                                    >
                                        <span className='c-btn__text'>
                                            {t(
                                                PAYMENT_INFORMATION_DETAILS_BUTTONS_EDIT,
                                            )}
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default FormSectionPage;
